import { useQuery } from '@apollo/client'
import { Autocomplete, TextField } from '@mui/material'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { GetOfficeLocationsByName } from './OfficeLocationsAutocomplete.gql'
import { OfficeLocationsAutocompleteProps } from './OfficeLocationsAutocompleteProps'

const LIMIT = 25
/**
 * multiple + single autocompletes are separated to workaround MUI typing
 * @param OfficeLocationsAutocompleteProps
 * @returns MUI Autocomplete with paginated options (single-select)
 */
const OfficeLocationsAutocomplete: FC<
    OfficeLocationsAutocompleteProps<false>
> = ({
    error,
    helperText,
    size,
    placeholder,
    label = 'Office Location',
    ...props
}) => {
    const [name, setName] = useState('')
    const [debounceName, setDebouncedName] = useState(name)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedName(name)
            nextPageToFetch.current = 2
        }, 500)

        return () => {
            clearTimeout(handler)
        }
    }, [name])

    const { data, fetchMore, loading } = useQuery(GetOfficeLocationsByName, {
        variables: { name: debounceName, page: 1, limit: LIMIT },
        fetchPolicy: 'network-only',
        skip: !open,
    })

    const loadMoreData = useCallback(() => {
        fetchMore({
            variables: {
                page: nextPageToFetch.current,
                limit: LIMIT,
                name: debounceName,
            },
            updateQuery(
                previousData,
                { fetchMoreResult, variables: { page, limit } }
            ) {
                if (
                    !previousData?.officeLocations?.data ||
                    !fetchMoreResult?.officeLocations?.data
                )
                    return fetchMoreResult ?? previousData
                const updatedCompaniesData = Array.from(
                    previousData.officeLocations.data
                )
                const pageLimit =
                    previousData.officeLocations.pageInfo.pageTotal
                nextPageToFetch.current =
                    page + 1 <= pageLimit ? page + 1 : page
                for (
                    let i = 0;
                    i < fetchMoreResult.officeLocations.data.length;
                    i++
                ) {
                    updatedCompaniesData[(page - 1) * limit + i] =
                        fetchMoreResult.officeLocations.data[i]
                }
                const newCache = {
                    ...previousData,
                    officeLocations: {
                        ...fetchMoreResult.officeLocations,
                        data: updatedCompaniesData,
                    },
                }
                if (
                    fetchMoreResult?.officeLocations?.pageInfo.itemTotal &&
                    fetchMoreResult.officeLocations.pageInfo.itemTotal ===
                        updatedCompaniesData.length
                ) {
                    observer.current?.disconnect()
                }
                return newCache
            },
        })
    }, [fetchMore, debounceName])

    const observerCallback = useCallback(
        (entries) => {
            if (entries[0].isIntersecting) {
                loadMoreData()
            }
        },
        [loadMoreData]
    )

    const observedElementRef = useRef<HTMLLIElement | null>(null)
    const nextPageToFetch = useRef<number>(2)
    const observer = useRef<IntersectionObserver>(
        new IntersectionObserver(observerCallback)
    )

    useEffect(() => {
        observer.current = new IntersectionObserver(observerCallback)
        // update which element we are observing as data changes
        if (observedElementRef.current) {
            observer.current?.observe(observedElementRef.current)
        }

        return () => {
            if (observer.current) observer.current.disconnect()
        }
        // open is a dependency because we lose the observedElementRef when the dropdown closes and need to re-observe it once we open again
    }, [observerCallback, open, data?.officeLocations.data])

    return (
        <Autocomplete
            fullWidth
            filterOptions={(x) => x}
            disablePortal
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            open={open}
            loading={loading}
            getOptionLabel={(option) => option.value}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option, state, ownerState) => {
                const index = state.index
                const totalOptions = ownerState.options.length
                const isObserver = index === totalOptions - 1
                return (
                    <li
                        {...props}
                        key={option.id}
                        ref={isObserver ? observedElementRef : null}
                    >
                        {option.value}
                    </li>
                )
            }}
            onInputChange={(e, value, reason) => {
                if (reason !== 'reset') setName(value)
            }}
            {...props}
            options={data?.officeLocations.data || []}
            renderInput={(params) => (
                <TextField
                    {...params}
                    size={size}
                    label={label}
                    variant="outlined"
                    error={error}
                    helperText={helperText}
                    placeholder={placeholder}
                />
            )}
        />
    )
}

export default OfficeLocationsAutocomplete
